import { useCallback, useEffect, useState } from 'react';
import type { ListServersResponse, ListUsersResponse, MeResponse, ServerRecord, ServerView, UserRecord } from '@hearth/shared';
import { serverHandle, serverName, sizeOf } from '@hearth/shared';
import type { Api } from '../api';
import { linkTo } from '../router';
import { Campfire } from '../ui/Campfire';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Avatar } from './Avatar';
import { campfireFor, errorText, statusText, timeAgo } from './serverWords';

// /app/admin (M10.17, admins): who's waiting for approval, who's approved, every server. The
// routes behind it refuse anyone else (twice: the User λ's admin gate, then each operation), so
// the page's own check only keeps others from seeing an empty shell.

/** Every page of a listing, following its cursor (a few pages at most at this size). */
async function everyPage<T>(load: (cursor: string | undefined) => Promise<{ items: T[]; cursor: string | undefined }>): Promise<T[]> {
  const all: T[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 50; page++) {
    const out = await load(cursor);
    all.push(...out.items);
    if (!out.cursor) break;
    cursor = out.cursor;
  }
  return all;
}

/** The name to show: as the top bar does, then the email's name. */
export const personName = (u: UserRecord) => u.displayName ?? u.name ?? u.username ?? u.email?.split('@')[0] ?? 'Someone';
/** Google, Discord, or Password (a user made by the owner in Cognito). */
export const providerName = (u: UserRecord) => (!u.provider || u.provider === 'Cognito' ? 'Password' : u.provider);

/** A whole record as the server list words read it (an admin's view of any server). */
export function asView(s: ServerRecord): ServerView {
  return {
    serverId: s.serverId,
    name: serverName(s),
    handle: s.handle ?? serverHandle(serverName(s), s.serverId),
    role: 'admin',
    game: s.game,
    region: s.region,
    version: s.version,
    status: s.status,
    idleStopMinutes: s.idleStopMinutes ?? 30,
    restorePending: !!s.restoreKey,
    size: sizeOf(s),
    ...(s.playersOnline !== undefined ? { playersOnline: s.playersOnline } : {}),
    ...(s.lastPlayedAt ? { lastPlayedAt: s.lastPlayedAt } : {}),
  };
}

export function AdminPage({ api, me }: { api: Api; me: MeResponse }) {
  const [users, setUsers] = useState<UserRecord[]>();
  const [servers, setServers] = useState<ServerRecord[]>();
  const [asking, setAsking] = useState<UserRecord>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const after = (cursor: string | undefined) => (cursor ? `?cursor=${encodeURIComponent(cursor)}` : '');
      const [u, s] = await Promise.all([
        everyPage(async (c) => {
          const r = await api.get<ListUsersResponse>(`/v1/admin/users${after(c)}`);
          return { items: r.users, cursor: r.cursor };
        }),
        everyPage(async (c) => {
          const r = await api.get<ListServersResponse>(`/v1/admin/servers${after(c)}`);
          return { items: r.servers, cursor: r.cursor };
        }),
      ]);
      setUsers(u);
      setServers(s);
      setError('');
    } catch (err) {
      setError(errorText(err));
    }
  }, [api]);
  // Only admins ask (the API would refuse anyone else anyway).
  useEffect(() => {
    if (me.admin) void load();
  }, [load, me.admin]);

  async function setApproved(user: UserRecord, approved: boolean) {
    setBusy(true);
    try {
      await api.post(`/v1/admin/users/${encodeURIComponent(user.userId)}/approval`, { approved });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
      setAsking(undefined);
    }
  }

  if (!me.admin) {
    return (
      <div className="card">
        <h1>Admins only</h1>
        <a {...linkTo('/app')}>Go to your servers</a>
      </div>
    );
  }
  return (
    <>
      <h1 className="page-title">Admin</h1>
      {error && <p className="notice" role="alert">{error}</p>}
      <AdminView users={users} servers={servers} meId={me.userId} now={new Date()} busy={busy} onApprove={(u) => void setApproved(u, true)} onUnapprove={setAsking} />
      <ConfirmDialog
        open={!!asking}
        title={`Unapprove ${asking ? personName(asking) : ''}?`}
        confirmLabel="Unapprove"
        tone="danger"
        busy={busy}
        onConfirm={() => asking && void setApproved(asking, false)}
        onCancel={() => setAsking(undefined)}
      >
        {asking ? `${personName(asking)} won't be able to create servers. Servers they already have keep running, and their friends can still wake them.` : ''}
      </ConfirmDialog>
    </>
  );
}

/** The admin page, given what's loaded (no fetching: easy to test). */
export function AdminView(props: {
  users: UserRecord[] | undefined;
  servers: ServerRecord[] | undefined;
  meId: string;
  now: Date;
  busy: boolean;
  onApprove: (user: UserRecord) => void;
  onUnapprove: (user: UserRecord) => void;
}) {
  const { users, servers, meId, now, busy, onApprove, onUnapprove } = props;
  if (!users || !servers) return <p className="quiet" aria-live="polite">Loading…</p>;
  const owned = (userId: string) => servers.filter((s) => s.ownerId === userId).length;
  const byName = (a: UserRecord, b: UserRecord) => personName(a).localeCompare(personName(b));
  // Waiting: the newest sign-ins first. You never: admins need no approval (the API refuses your own).
  const pending = users.filter((u) => !u.approved && u.userId !== meId).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const approved = users.filter((u) => u.approved || u.userId === meId).sort(byName);
  const ownerName = (s: ServerRecord) => {
    const owner = users.find((u) => u.userId === s.ownerId);
    return owner ? personName(owner) : 'an admin (before accounts)';
  };
  const person = (u: UserRecord, detail: string) => (
    <>
      <Avatar name={personName(u)} picture={u.picture} />
      <span className="person-name admin-who">
        <span>
          {personName(u)}
          {u.userId === meId && <span className="quiet"> (you)</span>}
        </span>
        <span className="quiet admin-meta">{[providerName(u), u.email, detail].filter(Boolean).join(' · ')}</span>
      </span>
    </>
  );

  return (
    <>
      <section className="panel">
        <h2>{`Waiting for approval (${pending.length})`}</h2>
        {pending.length === 0 ? (
          <p className="quiet">Nobody is waiting.</p>
        ) : (
          <ul className="people">
            {pending.map((u) => (
              <li key={u.userId}>
                {person(u, `first seen ${timeAgo(u.createdAt, now)}`)}
                <button className="btn btn-primary btn-small" type="button" disabled={busy} aria-label={`Approve ${personName(u)}`} onClick={() => onApprove(u)}>
                  Approve
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="panel">
        <h2>{`Approved (${approved.length})`}</h2>
        <ul className="people">
          {approved.map((u) => (
            <li key={u.userId}>
              {person(u, `last seen ${timeAgo(u.lastSeenAt, now)} · ${owned(u.userId) === 1 ? '1 server' : `${owned(u.userId)} servers`}`)}
              {u.userId !== meId && (
                <button className="quiet-btn quiet-danger" type="button" disabled={busy} aria-label={`Unapprove ${personName(u)}`} onClick={() => onUnapprove(u)}>
                  Unapprove
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section className="panel">
        <h2>{`Every server (${servers.length})`}</h2>
        {servers.length === 0 ? (
          <p className="quiet">No servers yet.</p>
        ) : (
          <ul className="people">
            {servers.map((s) => {
              const view = asView(s);
              return (
                <li key={s.serverId}>
                  <span className="admin-fire"><Campfire state={campfireFor(view)} height={20} still /></span>
                  <span className="person-name admin-who">
                    <a {...linkTo(`/app/servers/${encodeURIComponent(s.serverId)}`)}>{view.name}</a>
                    <span className="quiet admin-meta">{`${ownerName(s)} · Minecraft ${s.version} · ${statusText(view, now)}`}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}
